import { ReactNode, SyntheticEvent, useMemo } from 'react';
import { Ellipse, ErrorFill16, SuccessLine16, SuggestionFill16 } from 'ui/icons';
import { BUTTON_SIZES, BUTTON_VARIANTS, Button } from 'ui/button';
import {
  openNodeRequest,
  selectNodes,
  updateHasUnsavedChanges,
  updateNodeComboId,
} from 'modules/workflows/ducks';
import { useGetJourneyOptimizations, useOptimizeJourney } from 'modules/workflows/hooks';
import { Optimization } from 'modules/workflows/types';
import {
  useJourneyReviewContext,
  parseFetchedOptimizations,
} from 'modules/workflows/context/JourneyReviewContext';
import { useFlowChart, focusNode, FocusPosition, DEFAULT_PADDING } from 'ui/workflows/flowchart';
import { REVIEW_PANEL_WIDTH } from 'ui/workflows/nodeTilePanel/NodeTilePanel.styled';
import { useDispatch } from 'react-redux';
import { colors } from 'ui/theme';
import {
  JourneyReviewListItemContainer,
  JourneyReviewListItemBody,
  JourneyReviewListItemBodyHeader,
  JourneyReviewListItemBodyDescription,
  JourneyReviewListItemBodyFooter,
  JourneyReviewListContainer,
  styleLookUp,
} from './JourneyReviewList.styles';
import { TileReviewItem } from '../JourneyReview.types';
import { ItemStatus } from '../JourneyReview.constants';

// When journey review is open, the center of the canvas needs to be shifted slightly to the left to appear centered
const CANVAS_SHIFT_X = (-REVIEW_PANEL_WIDTH + DEFAULT_PADDING) / 2;
const DURATION = 400;

export const JOURNEY_REVIEW_ITEM = 'journey-review-item';
export const JOURNEY_ISSUE_ITEM_TEST_ID = 'journey-issue-item';
export const JOURNEY_PERFORMANCE_ITEM_TEST_ID = 'journey-performance-item';
export const JOURNEY_REVIEW_ITEM_COMPLETE_TEST_ID = 'journey-review-item-complete';

export const getReviewItemTestId = (testId: string, tileId: string) => `${testId}-${tileId}`;
export const getReviewItemEditButton = (testId: string, tileId: string) =>
  `${testId}-${tileId}-edit`;
export const getReviewItemCompleteIcon = (testId: string, tileId: string) =>
  `${testId}-${tileId}-complete`;

const JourneyReviewItem = ({
  Icon,
  Action,
  item,
  ...props
}: {
  Icon: any;
  Action?: any;
  item: TileReviewItem;
  [key: string]: any;
}) => {
  const { tileType, tileId, status, title, description } = item;

  return (
    <JourneyReviewListItemContainer data-test={JOURNEY_REVIEW_ITEM} {...props}>
      <Icon color={styleLookUp[status].icon} />
      <JourneyReviewListItemBody>
        <JourneyReviewListItemBodyHeader $statusStyle={status}>
          {title}
        </JourneyReviewListItemBodyHeader>
        {description && (
          <JourneyReviewListItemBodyDescription>{description}</JourneyReviewListItemBodyDescription>
        )}
        {tileType && tileId && (
          <JourneyReviewListItemBodyFooter>
            {tileType} <Ellipse height={3} width={3} /> Tile ID: {tileId}
          </JourneyReviewListItemBodyFooter>
        )}
      </JourneyReviewListItemBody>
      <Action />
    </JourneyReviewListItemContainer>
  );
};

const JourneyReviewList: React.FC<React.PropsWithChildren> = ({
  children,
}: {
  children?: ReactNode;
}) => {
  return <JourneyReviewListContainer>{children}</JourneyReviewListContainer>;
};

const JourneyIssueItem = ({ issue }: { issue: TileReviewItem }) => {
  const dispatch = useDispatch();
  const { graph } = useFlowChart();

  const handleItemClick = (callback?: () => void) => {
    if (issue.tileId) {
      dispatch(selectNodes([issue.tileId]));
      if (graph)
        focusNode(
          graph,
          issue.tileId,
          FocusPosition.Center,
          true,
          { duration: DURATION, ...(callback && { callback }) },
          { x: CANVAS_SHIFT_X, y: 0 },
        );
    }
  };

  const EditButton = () => (
    <Button
      data-test={getReviewItemEditButton(JOURNEY_ISSUE_ITEM_TEST_ID, issue.tileId)}
      onClick={(e) => {
        e.stopPropagation();
        handleItemClick(() => {
          dispatch(openNodeRequest(issue?.tileId, {}));
        });
      }}
      size={BUTTON_SIZES.SMALL}
      variant={BUTTON_VARIANTS.SECONDARY}
    >
      Edit
    </Button>
  );
  const SuccessLine = () => (
    <SuccessLine16
      data-test={getReviewItemCompleteIcon(JOURNEY_ISSUE_ITEM_TEST_ID, issue?.tileId)}
      height={24}
      width={24}
      color={colors.iconSuccess}
    />
  );

  return (
    <JourneyReviewItem
      data-test={getReviewItemTestId(JOURNEY_ISSUE_ITEM_TEST_ID, issue.tileId)}
      item={issue}
      Icon={ErrorFill16}
      Action={issue.status === ItemStatus.Error ? EditButton : SuccessLine}
      onClick={issue.status === ItemStatus.Error ? () => handleItemClick() : undefined}
    />
  );
};

const JourneyPerformanceItem = ({
  optimization,
  shouldRefetchOptimizations,
}: {
  optimization: TileReviewItem;
  shouldRefetchOptimizations?: boolean;
}) => {
  const dispatch = useDispatch();
  const { graph } = useFlowChart();
  const { completeOptimizations, optimizations } = useJourneyReviewContext();

  const handleFetchNewOptimizations = (data: Optimization[]) => {
    const optimizationData = parseFetchedOptimizations(data);

    // Complete any optimization that is not in the fetched data
    const completed = optimizations?.reduce((acc: string[], optItem) => {
      if (!optimizationData.find((item) => item.comboId === optItem.tileId)) {
        return [...acc, optItem.tileId];
      }
      return acc;
    }, []);
    completeOptimizations(completed);
  };

  const { getOptimizations, isLoading: isFetchingOptimizations } = useGetJourneyOptimizations({
    onGetOptimizationsSuccess: handleFetchNewOptimizations,
    onGetOptimizationsError: () => {
      completeOptimizations([optimization.tileId]);
    },
  });

  const { optimizeReviewItem, isOptimizing } = useOptimizeJourney();

  const handleApplyButtonClick = (e: SyntheticEvent) => {
    e.stopPropagation();
    optimizeReviewItem({
      nodeIds: optimization.tileIds || [],
      optimizationType: optimization.optimizationType || '',
      onSuccess: () => {
        dispatch(updateHasUnsavedChanges(true));
        if (shouldRefetchOptimizations) {
          getOptimizations();
        } else {
          completeOptimizations([optimization.tileId]);
        }
      },
    });
  };

  const handleItemClick = () => {
    if (optimization.tileId && optimization.tileIds) {
      const ids = optimization.tileIds[0]?.map((id: number) => id.toString()) || [];
      dispatch(selectNodes(ids));
      if (shouldRefetchOptimizations) dispatch(updateNodeComboId(ids, optimization.tileId));
      if (graph)
        focusNode(
          graph,
          optimization.tileId,
          FocusPosition.Center,
          true,
          { duration: DURATION },
          { x: CANVAS_SHIFT_X, y: 0 },
        );
    }
  };

  const isLoading = isOptimizing || isFetchingOptimizations;

  const ApplyButton = () => (
    <Button
      data-test={getReviewItemEditButton(
        JOURNEY_PERFORMANCE_ITEM_TEST_ID,
        optimization.tileId || '',
      )}
      onClick={handleApplyButtonClick}
      size={BUTTON_SIZES.SMALL}
      variant={BUTTON_VARIANTS.SECONDARY}
      isLoading={isLoading}
    >
      Apply
    </Button>
  );

  const SuccessLine = () => <SuccessLine16 height={24} width={24} color={colors.iconSuccess} />;

  return (
    <JourneyReviewItem
      item={{
        ...optimization,
        status: isLoading ? ItemStatus.Complete : optimization.status,
      }}
      Icon={SuggestionFill16}
      Action={optimization.status === ItemStatus.Complete ? SuccessLine : ApplyButton}
      onClick={
        optimization.status === ItemStatus.Incomplete && !isLoading ? handleItemClick : undefined
      }
    />
  );
};

export const JourneyIssueList = ({ issues = [] }: { issues: TileReviewItem[] }) => {
  return (
    <JourneyReviewList>
      {issues.map((issue: any) => {
        return <JourneyIssueItem key={issue.tileId} issue={issue} />;
      })}
    </JourneyReviewList>
  );
};

const nodeIdsToOptimization = (optimizations: TileReviewItem[] = []) =>
  optimizations.reduce((acc: Record<string, string[]>, opt) => {
    opt.tileIds?.flat()?.forEach((id) => {
      acc[id] = [...(acc[id] || []), opt.tileId];
    });
    return acc;
  }, {});

export const JourneyPerformanceList = ({
  optimizations = [],
}: {
  optimizations: TileReviewItem[];
}) => {
  const optimizationsByTileId = useMemo(
    () => nodeIdsToOptimization(optimizations),
    [optimizations],
  );

  return (
    <JourneyReviewList>
      {optimizations.map((optimization: TileReviewItem) => {
        // Refetch optimizations if a tile ID belongs to multiple optimizations
        const shouldRefetch = (optimization.tileIds || [])
          .flat()
          .some((id) => optimizationsByTileId[id]?.length > 1);
        return (
          <JourneyPerformanceItem
            key={optimization.tileId}
            optimization={optimization}
            shouldRefetchOptimizations={shouldRefetch}
          />
        );
      })}
    </JourneyReviewList>
  );
};
